import request from '@/utils/request'
import { readBlobBusinessError } from '@/utils/blob'
import { Message } from 'element-ui'

const DEFAULT_MEDIA_CDN_ORIGIN = 'https://d.cdn.aii.ndlink.cn'

export function normalizeMediaId(value) {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) return ''
  const id = String(value == null ? '' : value)
  return /^[1-9]\d{0,18}$/.test(id) ? id : ''
}

function applicationUrl(value) {
  const parsed = new URL(String(value || ''), `${window.location.origin}/`)
  if (parsed.origin !== window.location.origin || parsed.username || parsed.password ||
      !/^https?:$/.test(parsed.protocol)) {
    throw new Error('媒体应用地址必须与当前网站同源')
  }
  return parsed
}

export function extractMediaIdFromLegacyUrl(value) {
  try {
    const parsed = applicationUrl(value)
    if (!/\/(?:api\/|view\/)?media\/view\.action$|\/interviews\/legacy\/media$/.test(parsed.pathname)) return ''
    const values = parsed.searchParams.getAll('mediaId')
    return values.length === 1 ? normalizeMediaId(values[0]) : ''
  } catch (error) {
    return ''
  }
}

export function storedMediaId(value) {
  return normalizeMediaId(value) || extractMediaIdFromLegacyUrl(value)
}

function cdnUrl(value, publicDelivery) {
  const raw = String(value || '')
  const parsed = new URL(raw)
  const configured = new URL(process.env.VUE_APP_MEDIA_CDN_ORIGIN || DEFAULT_MEDIA_CDN_ORIGIN)
  if (parsed.protocol !== 'https:' || parsed.origin !== configured.origin ||
      parsed.username || parsed.password || parsed.hash) {
    throw new Error('媒体 CDN 地址不合法')
  }
  if (!publicDelivery && !parsed.search) throw new Error('媒体下载地址不合法')
  if (publicDelivery && (parsed.origin !== DEFAULT_MEDIA_CDN_ORIGIN || parsed.pathname === '/' ||
      raw.includes('?') || raw.includes('#') || /[\\\s]/.test(raw))) {
    throw new Error('媒体下载地址不合法')
  }
  return parsed.href
}

function safeFileName(value) {
  // File names must not carry path separators or header control characters.
  // eslint-disable-next-line no-control-regex
  return String(value || '').replace(/[\\/\u0000-\u001f\u007f]/g, '_').trim()
}

async function completeBlob(blob, metadata) {
  const message = await readBlobBusinessError(blob)
  if (message) throw new Error(message)
  const result = !blob.type && metadata.mimeType ? new Blob([blob], { type: metadata.mimeType }) : blob
  const fileName = safeFileName(metadata.fileName)
  if (fileName) Object.defineProperty(result, 'fileName', { value: fileName, configurable: true })
  return result
}

async function fetchAuthorizedMediaBlob(mediaId) {
  const id = normalizeMediaId(mediaId)
  if (!id) throw new Error('媒体 ID 不合法')
  for (let attempt = 0; attempt < 2; attempt++) {
    // The authenticated client is used only for the application's authorization check.
    const metadata = await request({ url: `/media/${id}/delivery`, method: 'get' })
    if (!metadata || normalizeMediaId(metadata.mediaId) !== id) throw new Error('媒体下载信息不匹配')
    if (metadata.deliveryMode === 'application') {
      const target = applicationUrl(metadata.url)
      if (extractMediaIdFromLegacyUrl(target.href) !== id) throw new Error('媒体应用地址与媒体 ID 不匹配')
      const blob = await request({ url: target.href, method: 'get', responseType: 'blob' })
      return completeBlob(blob, metadata)
    }
    if (metadata.deliveryMode !== 'cdn') throw new Error('未知的媒体下载方式')
    if (metadata.cdnAuthRequired !== undefined && typeof metadata.cdnAuthRequired !== 'boolean') {
      throw new Error('媒体下载信息不合法')
    }
    // Only an explicit false from the authorized server enables public delivery.
    // Older metadata without this field must retain its signed-link expiry checks.
    const publicDelivery = metadata.cdnAuthRequired === false
    const target = cdnUrl(metadata.url, publicDelivery)
    if (publicDelivery && metadata.expiresAt != null) throw new Error('媒体下载信息不合法')
    if (!publicDelivery && (!Number.isFinite(Number(metadata.expiresAt)) || Number(metadata.expiresAt) <= Date.now() + 1000)) {
      if (!attempt) continue
      throw new Error('媒体下载地址已过期，请重试')
    }
    // Never forward the application's Bearer token or cookies to a CDN host.
    let response
    try {
      response = await window.fetch(target, {
        method: 'GET', mode: 'cors', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer'
      })
    } catch (error) {
      // No HTTP response: use the server-advertised, independently authorized stream once.
      // Never treat a 401/403, malformed metadata, or an expired URL as permission to fall back.
      if (!metadata.fallbackUrl) throw error
      const fallback = applicationUrl(metadata.fallbackUrl)
      if (extractMediaIdFromLegacyUrl(fallback.href) !== id ||
          Array.from(fallback.searchParams.keys()).some(key => key !== 'mediaId')) {
        throw new Error('媒体备用地址与媒体 ID 不匹配')
      }
      const blob = await request({ url: fallback.href, method: 'get', responseType: 'blob' })
      return completeBlob(blob, metadata)
    }
    if (!response.ok) {
      if (!attempt && (response.status === 403 || response.status === 410)) continue
      throw new Error(`媒体下载失败（${response.status}）`)
    }
    return completeBlob(await response.blob(), metadata)
  }
  throw new Error('媒体下载地址已过期，请重试')
}

export async function fetchMediaBlob(mediaId) {
  try {
    return await fetchAuthorizedMediaBlob(mediaId)
  } catch (error) {
    if (!(error && (error.notified || error.authExpired))) {
      Message.error((error && error.message) || '媒体获取失败，请重试')
      if (error) error.notified = true
    }
    throw error
  }
}

// Generated files without a stored-media ID retain their original application URL.
export async function resolveStoredMediaPreviewUrl(value, mediaId) {
  const id = storedMediaId(mediaId) || extractMediaIdFromLegacyUrl(value)
  if (!id) return String(value || '')
  return window.URL.createObjectURL(await fetchMediaBlob(id))
}

export function revokeMediaPreviewUrl(value) {
  if (String(value || '').startsWith('blob:')) window.URL.revokeObjectURL(value)
}
